/**
 * Animated GIF Helper for PlayCanvas
 *
 * Uses gifuct-js to parse and decompress GIF frames, then renders them
 * to a PlayCanvas texture with proper transparency support.
 */
import * as pc from 'playcanvas';
import { parseGIF, decompressFrames } from 'gifuct-js';
/**
 * Represents a single GIF frame
 */
interface GifFrame {
    dims: {
        width: number;
        height: number;
        top: number;
        left: number;
    };
    patch: Uint8ClampedArray;
    delay: number;
    disposalType?: number;
}
/**
 * Options for creating an animated GIF texture
 */
export interface AnimatedGifOptions {
    /** Auto-play the GIF when loaded */
    autoPlay?: boolean;
    /** Callback when the first frame is ready */
    onReady?: () => void;
    /** Callback on error */
    onError?: (error: Error) => void;
}
/**
 * Animated GIF texture for PlayCanvas
 * Handles parsing, decompression, and frame-by-frame rendering with transparency
 */
export class AnimatedGifTexture {
    private app: pc.Application;
    private url: string;
    private frames: GifFrame[] = [];
    private currentFrameIndex = 0;
    private isPlaying = false;
    private isLoaded = false;
    /** Set by destroy(): a load still in flight then stops without creating a texture or
     *  calling back, so a marker removed while its GIF downloads leaks nothing. */
    private destroyed = false;
    private lastFrameTime = 0;
    private updateHandler: (() => void) | null = null;
    private options: AnimatedGifOptions;
    // Canvas for compositing frames (handles disposal and transparency)
    private canvas: HTMLCanvasElement;
    private ctx: CanvasRenderingContext2D;
    // Reusable patch canvas for drawFrame — sized exactly per frame (the width/height assignment
    // clears it, so no stale pixels from previously larger frames can bleed through).
    private tempCanvas: HTMLCanvasElement | null = null;
    private tempCtx: CanvasRenderingContext2D | null = null;
    // PlayCanvas texture
    public texture: pc.Texture | null = null;
    // GIF dimensions
    private gifWidth = 0;
    private gifHeight = 0;
    constructor(app: pc.Application, url: string, options: AnimatedGifOptions = {}) {
        this.app = app;
        this.url = url;
        this.options = options;
        // Create offscreen canvas for compositing. No willReadFrequently: the canvas is never read
        // back — it feeds the texture directly via setSource/upload, and the hint would force a
        // software-backed canvas, defeating GPU-side canvas→texture transfer.
        this.canvas = document.createElement('canvas');
        this.ctx = this.canvas.getContext('2d')!;
        // Start loading
        this.load();
    }
    /**
     * Load and parse the GIF
     */
    private async load(): Promise<void> {
        try {
            // Fetch GIF data
            const response = await fetch(this.url);
            if (this.destroyed) return;
            if (!response.ok) {
                throw new Error(`Failed to fetch GIF: ${response.statusText}`);
            }
            const buffer = await response.arrayBuffer();
            if (this.destroyed) return;
            // Parse GIF
            const gif = parseGIF(buffer);
            this.frames = decompressFrames(gif, true) as unknown as GifFrame[];
            if (this.frames.length === 0) {
                throw new Error('GIF has no frames');
            }
            // Get dimensions from first frame
            this.gifWidth = gif.lsd.width;
            this.gifHeight = gif.lsd.height;
            // Setup canvas
            this.canvas.width = this.gifWidth;
            this.canvas.height = this.gifHeight;
            // Create PlayCanvas texture
            this.texture = new pc.Texture(this.app.graphicsDevice, {
                width: this.gifWidth,
                height: this.gifHeight,
                format: pc.PIXELFORMAT_RGBA8,
                mipmaps: false,
                minFilter: pc.FILTER_LINEAR,
                magFilter: pc.FILTER_LINEAR,
                addressU: pc.ADDRESS_CLAMP_TO_EDGE,
                addressV: pc.ADDRESS_CLAMP_TO_EDGE
            });
            // Feed the texture straight from the compositing canvas: the engine uploads
            // canvas-backed textures via texImage2D (same pattern as uploadVideoTextureFrame),
            // so per-frame updates are just texture.upload() — no getImageData readback, no
            // lock/set pixel copies. Canvas dims equal texture dims, so no resize occurs.
            this.texture.setSource(this.canvas);
            // Draw first frame
            this.drawFrame(0);
            this.isLoaded = true;
            console.log(`[AnimatedGif] Loaded GIF: ${this.url}, ${this.frames.length} frames, ${this.gifWidth}x${this.gifHeight}`);
            // Notify ready
            if (this.options.onReady) {
                this.options.onReady();
            }
            // Auto-play if requested
            if (this.options.autoPlay) {
                this.play();
            }
        }
        catch (error) {
            if (this.destroyed) return;
            console.error('[AnimatedGif] Error loading GIF:', error);
            if (this.options.onError) {
                this.options.onError(error instanceof Error ? error : new Error(String(error)));
            }
        }
    }
    /**
     * Draw a specific frame to the canvas and update the texture
     */
    private drawFrame(frameIndex: number): void {
        if (!this.texture || frameIndex >= this.frames.length)
            return;
        const frame = this.frames[frameIndex];
        const prevFrame = frameIndex > 0 ? this.frames[frameIndex - 1] : null;
        // Handle disposal of previous frame
        if (prevFrame && prevFrame.disposalType === 2) {
            // Restore to background (clear the previous frame area)
            this.ctx.clearRect(prevFrame.dims.left, prevFrame.dims.top, prevFrame.dims.width, prevFrame.dims.height);
        }
        // disposalType === 3 (restore to previous) is complex and rarely used, skip for now
        // Create ImageData from frame patch
        const imageData = new ImageData(new Uint8ClampedArray(frame.patch), frame.dims.width, frame.dims.height);
        // Reusable patch canvas (putImageData REPLACES pixels, so the patch must go through a
        // canvas and drawImage to composite transparency/disposal correctly onto the main canvas).
        // Exact-size assignment every frame also clears it.
        if (!this.tempCanvas) {
            this.tempCanvas = document.createElement('canvas');
            this.tempCtx = this.tempCanvas.getContext('2d')!;
        }
        this.tempCanvas.width = frame.dims.width;
        this.tempCanvas.height = frame.dims.height;
        this.tempCtx!.putImageData(imageData, 0, 0);
        // Draw frame patch at correct position
        this.ctx.drawImage(this.tempCanvas, frame.dims.left, frame.dims.top);
        // Update texture from canvas
        this.updateTexture();
    }
    /**
     * Update the PlayCanvas texture from the canvas
     */
    private updateTexture(): void {
        if (!this.texture)
            return;
        // The canvas is the texture's source (set once in load) — re-upload it directly.
        this.texture.upload();
    }
    /**
     * Animation update - called each frame when playing
     */
    private update = (): void => {
        if (!this.isPlaying || !this.isLoaded || this.frames.length <= 1)
            return;
        const now = performance.now();
        const frame = this.frames[this.currentFrameIndex];
        const delay = frame.delay || 100; // Default 100ms if not specified
        if (now - this.lastFrameTime >= delay) {
            // Advance to next frame
            this.currentFrameIndex = (this.currentFrameIndex + 1) % this.frames.length;
            this.drawFrame(this.currentFrameIndex);
            this.lastFrameTime = now;
        }
    };
    /**
     * Start playing the GIF animation
     */
    public play(): void {
        if (this.isPlaying || this.destroyed)
            return;
        this.isPlaying = true;
        this.lastFrameTime = performance.now();
        // Register update handler
        if (!this.updateHandler) {
            this.updateHandler = this.update;
            this.app.on('update', this.updateHandler);
        }
    }
    /**
     * Pause the GIF animation
     */
    public pause(): void {
        if (!this.isPlaying)
            return;
        this.isPlaying = false;
        // Remove update handler
        if (this.updateHandler) {
            this.app.off('update', this.updateHandler);
            this.updateHandler = null;
        }
    }
    /**
     * Stop and reset to first frame
     */
    public stop(): void {
        this.pause();
        this.currentFrameIndex = 0;
        if (this.isLoaded) {
            // Clear canvas and redraw first frame
            this.ctx.clearRect(0, 0, this.gifWidth, this.gifHeight);
            this.drawFrame(0);
        }
    }
    /**
     * Check if the GIF is currently playing
     */
    public get playing(): boolean {
        return this.isPlaying;
    }
    /**
     * Check if the GIF is loaded
     */
    public get loaded(): boolean {
        return this.isLoaded;
    }
    /**
     * Clean up resources
     */
    public destroy(): void {
        this.destroyed = true;
        this.pause();
        if (this.texture) {
            this.texture.destroy();
            this.texture = null;
        }
        this.frames = [];
        this.isLoaded = false;
        this.canvas = null!;
        this.ctx = null!;
    }
}
/**
 * Helper to detect if a URL is a GIF
 */
export function isGifUrl(url: string): boolean {
    const lower = url.toLowerCase();
    // Check extension or content type hint
    return lower.endsWith('.gif') || lower.includes('image/gif') || lower.includes('format=gif');
}
